import { Stack, Tooltip, useTheme } from '@mui/material';
import { useState } from 'react';

import { useDialog } from '@app/hooks';
import { Iconify } from '@app/components';
import { useAuthRolePermissions } from '@app/utils';
import { SvgColor } from '@app/components/svg-color';
import { CustomAvatar } from '@app/components/custom-avatar';
import { BoardDetailDialog } from './board-detail-dialog';
import { AddMemberDialog } from './add-member-dialog';
import { ChangeBackground } from './change-background-dialog';
import { ISideBarSchema } from '../types';
import { PermissionDialog } from './permissions-dialog';

interface props {
  data: ISideBarSchema;
  setSelectedManuallyImage?: any;
  isLoggedInUser: boolean;
}

const SideBar = ({ data, setSelectedManuallyImage, isLoggedInUser }: props) => {
  const boardDetailDialog = useDialog();
  const commingSoonDialog = useDialog();
  const addMemberDialog = useDialog();
  const changeBackgroundDialog = useDialog();
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState('');

  const { hasPermission } = useAuthRolePermissions();
  const canAddBackground = hasPermission('Board Images', 'w');
  const canAddBoardMember = hasPermission('Board Members', 'w');

  const boardDetails: Array<{ icon: string; iconName: string }> = [
    { icon: 'ic_boardDetail', iconName: 'Board Details' },
    ...(canAddBoardMember ? [{ icon: 'ic_member', iconName: 'Add Members' }] : []),
    ...(canAddBackground ? [{ icon: 'ic_background', iconName: 'Background' }] : []),
  ];

  const handleBoardOptionClick = (iconName: string) => () => {
    setSelectedOption(iconName);
    if (iconName === 'ic_boardDetail') {
      boardDetailDialog.show();
    } else if (iconName === 'ic_permissions') {
      commingSoonDialog.show();
    } else if (iconName === 'ic_member') {
      addMemberDialog.show();
    } else if (iconName === 'ic_background') {
      changeBackgroundDialog.show();
    }
  };

  return (
    <>
      <Stack
        sx={{
          width: '50px',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(15px)',
          borderTopLeftRadius: '30px',
          borderBottomLeftRadius: '30px',
          py: 2,
        }}
      >
        <Stack mt={2}>
          <CustomAvatar
            alt="profile image"
            sx={{
              width: 30,
              height: 30,
              alignSelf: 'center',
              backgroundColor: theme.palette.taskboard.darkSlateGray,
              color: theme.palette.common.white,
              fontSize: 14,
              fontWeight: 600,
            }}
            src={data?.user?.profileImage}
            name={`${data?.user?.firstName} ${data?.user?.lastName}`}
          />
          <Iconify
            icon="mdi:crown"
            style={{ color: 'white' }}
            width={12}
            sx={{ position: 'relative', left: '12px', bottom: '12px' }}
          />
        </Stack>

        <Stack sx={{ display: 'flex', margin: 0, padding: 0 }}>
          {boardDetails.map((board, index) => (
            <Tooltip
              arrow
              key={index}
              title={board.iconName}
              placement="top"
              PopperProps={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -6],
                    },
                  },
                ],
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderRight: selectedOption === board.icon ? '3px solid' : 'none',
                  borderColor: theme.palette.taskboard.raisinBlack,
                  height: '40px',
                  borderWidth: '5px',
                  borderRadius: '2px',
                }}
                gap={0.5}
                onClick={handleBoardOptionClick(board?.icon)}
              >
                <SvgColor
                  src={`/assets/icons/card/${board?.icon}.svg`}
                  sx={{
                    width: '24px',
                    height: '30px',
                    color: theme.palette.taskboard.darkSlateGray,
                  }}
                />
              </Stack>
            </Tooltip>
          ))}
        </Stack>

        {boardDetailDialog.visible && (
          <BoardDetailDialog
            open
            onClose={boardDetailDialog.hide}
            iconName={'ic_boardDetail'}
            data={data}
          />
        )}

        {addMemberDialog.visible && (
          <AddMemberDialog open onClose={addMemberDialog.hide} iconName={'ic_member'} />
        )}
        {changeBackgroundDialog.visible && (
          <ChangeBackground
            open
            onClose={changeBackgroundDialog.hide}
            iconName={'ic_background'}
            setSelectedManuallyImage={setSelectedManuallyImage}
          />
        )}
        {commingSoonDialog.visible && (
          <PermissionDialog open onClose={commingSoonDialog.hide} iconName="" />
        )}
      </Stack>
    </>
  );
};

export { SideBar };
